// Frontend Web Crypto API implementation for ECDH and AES-256-GCM

let sessionKey = null;
let sessionId = null;

export const generateKeyPair = async () => {
  const keyPair = await window.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits", "deriveKey"]
  );
  return keyPair;
};

// Convert ArrayBuffer to Base64
const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Convert Base64 to ArrayBuffer
const base64ToBuffer = (base64) => {
  const binary_string = window.atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
};

// Convert CryptoKey (Public) to SPKI format and then to raw Buffer format like Node uses, or we can use raw format
export const exportPublicKey = async (publicKey) => {
  // WebCrypto ECDH raw format exports exactly the 65 bytes needed by Node's createECDH
  const exported = await window.crypto.subtle.exportKey("raw", publicKey);
  return bufferToBase64(exported);
};

export const importServerPublicKey = async (base64Key) => {
  const rawKey = base64ToBuffer(base64Key);
  return await window.crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    []
  );
};

export const deriveSessionKey = async (privateKey, serverPublicKeyBase64) => {
  const serverPublicKey = await importServerPublicKey(serverPublicKeyBase64);
  
  // Derive raw bits
  const sharedSecretBits = await window.crypto.subtle.deriveBits(
    { name: "ECDH", public: serverPublicKey },
    privateKey,
    256
  );

  // Use HKDF to derive 32-byte AES key from shared secret, matching Node.js
  const hkdfKey = await window.crypto.subtle.importKey(
    "raw",
    sharedSecretBits,
    { name: "HKDF" },
    false,
    ["deriveKey"]
  );

  const encoder = new TextEncoder();
  const info = encoder.encode('securecheck-api-encryption');
  const salt = new Uint8Array(0);

  const aesKey = await window.crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: salt,
      info: info
    },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );

  return aesKey;
};

export const setSession = (id, key) => {
  sessionId = id;
  sessionKey = key;
};

export const clearSession = () => {
  sessionId = null;
  sessionKey = null;
};

export const getSessionId = () => sessionId;
export const getSessionKey = () => sessionKey;


export const encryptPayload = async (payloadObj) => {
  if (!sessionKey) return payloadObj;
  
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoder = new TextEncoder();
  const data = encoder.encode(JSON.stringify(payloadObj));

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    sessionKey,
    data
  );

  // encryptedBuffer contains both ciphertext and auth tag (last 16 bytes)
  const encryptedBytes = new Uint8Array(encryptedBuffer);
  const ciphertextBytes = encryptedBytes.slice(0, -16);
  const tagBytes = encryptedBytes.slice(-16);

  return {
    iv: bufferToBase64(iv.buffer),
    ciphertext: bufferToBase64(ciphertextBytes.buffer),
    tag: bufferToBase64(tagBytes.buffer)
  };
};

export const decryptPayload = async (encryptedPayload) => {
  if (!sessionKey || !encryptedPayload.iv || !encryptedPayload.ciphertext || !encryptedPayload.tag) {
    return encryptedPayload;
  }

  const iv = base64ToBuffer(encryptedPayload.iv);
  const ciphertext = new Uint8Array(base64ToBuffer(encryptedPayload.ciphertext));
  const tag = new Uint8Array(base64ToBuffer(encryptedPayload.tag));

  // Recombine ciphertext and tag
  const combined = new Uint8Array(ciphertext.length + tag.length);
  combined.set(ciphertext);
  combined.set(tag, ciphertext.length);

  try {
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(iv) },
      sessionKey,
      combined.buffer
    );

    const decoder = new TextDecoder();
    return JSON.parse(decoder.decode(decryptedBuffer));
  } catch (error) {
    console.error("Decryption failed", error);
    throw error;
  }
};
